import { __assign, __awaiter, __generator } from "tslib";
import { getEventPath } from '@tyler-components-web/core';
import { BOTTOM_SHEET_CONSTANTS } from './bottom-sheet-constants';
var BottomSheetFoundation = /** @class */ (function () {
    function BottomSheetFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._open = false;
        this._backdropClose = true;
        this._escapeClose = true;
        this._fullscreen = false;
        this._showBackdrop = false;
        this._isDragging = false;
        this._bodyScrollHandler = function () { return _this._onBodyScroll(); };
        this._dragStartHandler = function ($event) { return _this._onDragStart($event); };
        this._dragMoveHandler = function ($event) { return _this._onDragMove($event); };
        this._dragEndHandler = function () { return _this._onDragEnd(); };
        this._dragCancelHandler = function () { return _this._onDragCancel(); };
        this._openTransitionEndHandler = function (_evt) { return _this._onOpenTransitionEnd(); };
        this._closeTransitionEndHandler = function (_evt) { return _this._onCloseTransitionEnd(); };
        this._documentKeydownHandler = function (evt) { return _this._onDocumentKeydown(evt); };
        this._backdropClickHandler = function (evt) { return _this._onBackdropClick(evt); };
    }
    BottomSheetFoundation.prototype.initialize = function () {
        this._adapter.initializeAccessibility();
    };
    BottomSheetFoundation.prototype.destroy = function () {
        if (this._open) {
            this._adapter.deregisterTransitionEndHandler(this._openTransitionEndHandler);
            this._adapter.removeDocumentListener('keydown', this._documentKeydownHandler);
            this._adapter.deregisterBackdropClickHandler(this._backdropClickHandler);
        }
    };
    BottomSheetFoundation.prototype._setOpen = function (open) {
        this._open = open;
        if (this._open) {
            this._openBottomSheet();
        }
        else {
            this._closeBottomSheet();
        }
        this._adapter.toggleHostAttribute(BOTTOM_SHEET_CONSTANTS.attributes.OPEN, this._open);
    };
    BottomSheetFoundation.prototype._openBottomSheet = function () {
        var _this = this;
        this._adapter.attach();
        this._adapter.setBodyAttribute(BOTTOM_SHEET_CONSTANTS.attributes.OPEN, 'true');
        this._adapter.registerTransitionEndHandler(this._openTransitionEndHandler);
        this._setDocumentKeydownListener(this._escapeClose);
        this._setBackdropClickListener(this._backdropClose);
        // Ensure transitions are triggered properly
        window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
                _this._adapter.setVisibility(true);
                _this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.OPEN);
                _this._adapter.trySetInitialFocus();
                _this._adapter.initScrollable();
            });
        });
    };
    BottomSheetFoundation.prototype._closeBottomSheet = function () {
        this._adapter.deregisterTransitionEndHandler(this._openTransitionEndHandler);
        this._adapter.removeDocumentListener('keydown', this._documentKeydownHandler);
        this._adapter.deregisterBackdropClickHandler(this._backdropClickHandler);
        this._adapter.registerTransitionEndHandler(this._closeTransitionEndHandler);
        this._adapter.setVisibility(false);
    };
    BottomSheetFoundation.prototype._onCloseTransitionEnd = function () {
        this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.CLOSE);
        this._adapter.detach();
        var openBottomSheets = this._adapter.getOpenBottomSheets(BOTTOM_SHEET_CONSTANTS.elementName);
        if (!openBottomSheets.length) {
            this._adapter.removeBodyAttribute(BOTTOM_SHEET_CONSTANTS.attributes.OPEN);
        }
    };
    BottomSheetFoundation.prototype._onOpenTransitionEnd = function () {
        if (!this._open) {
            return;
        }
        this._adapter.deregisterTransitionEndHandler(this._openTransitionEndHandler);
        this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.READY);
        this._adapter.tryLayoutChildren();
        if (this._adapter.isScrollable()) {
            this._initScrollableHandlers();
        }
    };
    BottomSheetFoundation.prototype._onDocumentKeydown = function (evt) {
        evt.stopPropagation();
        if (evt.key && (evt.key === 'Escape' || evt.key === 'Esc')) {
            this._tryClose();
        }
    };
    BottomSheetFoundation.prototype._onBackdropClick = function (evt) {
        evt.stopPropagation();
        this._tryClose();
    };
    BottomSheetFoundation.prototype._tryClose = function () {
        return __awaiter(this, void 0, void 0, function () {
            var isCancelled, shouldClose, err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        isCancelled = !this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.BEFORE_CLOSE, undefined, undefined, true);
                        if (isCancelled) {
                            return [2 /*return*/];
                        }
                        if (!this._beforeCloseCallback) {
                            this.open = false;
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, Promise.resolve(this._beforeCloseCallback())];
                    case 2:
                        shouldClose = _a.sent();
                        if (shouldClose) {
                            this.open = false;
                            return [2 /*return*/];
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _a.sent();
                        return [2 /*return*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    BottomSheetFoundation.prototype._setBackdropClickListener = function (attach) {
        if (!this._open) {
            return;
        }
        if (attach) {
            this._adapter.registerBackdropClickHandler(this._backdropClickHandler);
        }
        else {
            this._adapter.deregisterBackdropClickHandler(this._backdropClickHandler);
        }
    };
    BottomSheetFoundation.prototype._setDocumentKeydownListener = function (attach) {
        if (!this._open) {
            return;
        }
        if (attach) {
            this._adapter.addDocumentListener('keydown', this._documentKeydownHandler);
        }
        else {
            this._adapter.removeDocumentListener('keydown', this._documentKeydownHandler);
        }
    };
    Object.defineProperty(BottomSheetFoundation.prototype, "backdropClose", {
        get: function () {
            return this._backdropClose;
        },
        /** Controls whether clicking the backdrop closes the bottom sheet or not. */
        set: function (value) {
            value = Boolean(value);
            if (this._backdropClose !== value) {
                this._backdropClose = value;
                this._setBackdropClickListener(this._backdropClose);
                this._adapter.toggleHostAttribute(BOTTOM_SHEET_CONSTANTS.attributes.BACKDROP_CLOSE, this._backdropClose);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BottomSheetFoundation.prototype, "escapeClose", {
        get: function () {
            return this._escapeClose;
        },
        /** Controls whether pressing the escape key closes the sheet or not. */
        set: function (value) {
            value = Boolean(value);
            if (this._escapeClose !== value) {
                this._escapeClose = value;
                this._setDocumentKeydownListener(this._escapeClose);
                this._adapter.toggleHostAttribute(BOTTOM_SHEET_CONSTANTS.attributes.ESCAPE_CLOSE, this._escapeClose);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BottomSheetFoundation.prototype, "open", {
        get: function () {
            return this._open;
        },
        set: function (value) {
            var _this = this;
            if (this._open !== value) {
                value = Boolean(value);
                if (value !== this._open) {
                    var callback = value ? this._openCallback : this._closeCallback;
                    if (callback) {
                        Promise.resolve(callback())
                            .then(function () {
                            _this._setOpen(value);
                        })
                            .catch(function () { });
                    }
                    else {
                        this._setOpen(value);
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BottomSheetFoundation.prototype, "fullscreen", {
        get: function () {
            return this._fullscreen;
        },
        set: function (value) {
            if (this._fullscreen !== value) {
                this._fullscreen = value;
                this._adapter.setFullscreen(this._fullscreen);
                this._adapter.toggleHostAttribute(BOTTOM_SHEET_CONSTANTS.attributes.FULLSCREEN, this._fullscreen);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BottomSheetFoundation.prototype, "showBackdrop", {
        get: function () {
            return this._showBackdrop;
        },
        set: function (value) {
            if (this._showBackdrop !== value) {
                this._showBackdrop = value;
                this._adapter.setBackdropVisiblity(value);
                this._adapter.toggleHostAttribute(BOTTOM_SHEET_CONSTANTS.attributes.SHOW_BACKDROP, this._showBackdrop);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BottomSheetFoundation.prototype, "openCallback", {
        set: function (callback) {
            this._openCallback = callback;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BottomSheetFoundation.prototype, "closeCallback", {
        set: function (callback) {
            this._closeCallback = callback;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BottomSheetFoundation.prototype, "beforeCloseCallback", {
        set: function (callback) {
            this._beforeCloseCallback = callback;
        },
        enumerable: true,
        configurable: true
    });
    /** If scrollable, allow drag to/from fullscreen, and make fullscreen on scroll. */
    BottomSheetFoundation.prototype._initScrollableHandlers = function () {
        if (!this._fullscreen && this._adapter.isScrollable()) {
            this._adapter.setDragTargetHandler('mousedown', this._dragStartHandler);
            this._adapter.setDragTargetHandler('touchstart', this._dragStartHandler);
            this._adapter.setBodyScrollHandler(this._bodyScrollHandler);
        }
    };
    BottomSheetFoundation.prototype._onBodyScroll = function () {
        if (!this._fullscreen && !this._isDragging) {
            this.fullscreen = true;
            this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.FULLSCREEN, this._fullscreen);
        }
    };
    BottomSheetFoundation.prototype._onDragStart = function (evt) {
        if (this._fullscreen) {
            var eventPath = getEventPath(evt);
            var isWithinScrollContainer = this._adapter.isScrollable() && this._adapter.isContentChild(eventPath[0]);
            if (isWithinScrollContainer) {
                // We ignore drag events that originate from within the scrollable content when in full screen mode (to allow for scroll interactions)
                return;
            }
        }
        evt.stopPropagation();
        var bounds = this._adapter.getContainerBounds();
        var clientY = evt instanceof MouseEvent ? evt.clientY : evt.touches[0].clientY;
        this._dragContext = {
            top: clientY - bounds.top,
            height: bounds.height
        };
        this._adapter.setBodyListener('mousemove', this._dragMoveHandler, { passive: false });
        this._adapter.setBodyListener('touchmove', this._dragMoveHandler, { passive: false });
        this._adapter.setBodyListener('mouseup', this._dragEndHandler);
        this._adapter.setBodyListener('touchend', this._dragEndHandler);
        this._adapter.setBodyListener('touchcancel', this._dragCancelHandler);
    };
    BottomSheetFoundation.prototype._onDragMove = function (evt) {
        evt.stopPropagation();
        evt.preventDefault();
        if (!this._dragContext) {
            return;
        }
        var clientY = evt instanceof MouseEvent ? evt.clientY : evt.touches[0].clientY;
        var height = { y: window.innerHeight - clientY + this._dragContext.top };
        // If this is the beginning of the move sequence, we emit the start event (to allow for preventing default) and
        // then update the surface position if not prevented
        if (!this._isDragging) {
            this._isDragging = true;
            var canDrag = this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.DRAG_START, height, true, true);
            if (!canDrag) {
                return;
            }
            this._adapter.setDragging(true);
        }
        // If not fullscreen, clamp to minimum of original height.
        var newPosition = this._fullscreen ? height : { y: Math.max(height.y, this._dragContext.height) };
        // Only update the position if it actually changed
        if (!this._lastPosition || newPosition.y !== this._lastPosition.y) {
            var canMove = this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.DRAGGED, newPosition, true, true);
            if (canMove) {
                this._lastPosition = __assign(__assign({}, newPosition), { clientY: clientY });
                this._adapter.setContainerHeight(newPosition.y);
            }
        }
    };
    BottomSheetFoundation.prototype._onDragEnd = function () {
        if (this._lastPosition) {
            var clientY = this._lastPosition.clientY;
            var dragContext = this._dragContext;
            if (dragContext) {
                if (this._fullscreen && clientY > 0) {
                    this.fullscreen = false;
                    this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.FULLSCREEN, this._fullscreen);
                }
                else if (!this._fullscreen && clientY < dragContext.height + dragContext.top) {
                    this.fullscreen = true;
                    this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.FULLSCREEN, this._fullscreen);
                }
            }
        }
        if (this._isDragging) {
            this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.DRAG_END);
        }
        this._dragComplete();
    };
    BottomSheetFoundation.prototype._onDragCancel = function () {
        if (this._isDragging) {
            this._adapter.emitHostEvent(BOTTOM_SHEET_CONSTANTS.events.DRAG_CANCEL);
        }
        this._dragComplete();
    };
    BottomSheetFoundation.prototype._dragComplete = function () {
        this._adapter.setDragging(false);
        this._adapter.removeBodyListener('mousemove', this._dragMoveHandler);
        this._adapter.removeBodyListener('touchmove', this._dragMoveHandler);
        this._adapter.removeBodyListener('mouseup', this._dragEndHandler);
        this._adapter.removeBodyListener('touchend', this._dragEndHandler);
        this._adapter.removeBodyListener('touchcancel', this._dragCancelHandler);
        this._adapter.setContainerHeight(null);
        this._lastPosition = undefined;
        this._dragContext = undefined;
        this._isDragging = false;
    };
    return BottomSheetFoundation;
}());
export { BottomSheetFoundation };
